// App.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useEffect } from 'react'
import {
  RouteObject, useRoutes, Navigate, useLocation
} from 'react-router-dom'
import { connect } from 'react-redux'
import AppShell from './components/AppShell'
import LoginPage from './pages/LoginPage'
import { routes, Route } from './routes'
import { SelectCompanyPage } from './pages/SelectCompanyPages'
import { AppThunkDispatch, RootState } from './redux'
import { validateSession } from './redux/users/thunks'

/**
 * Helper function to set up children routes for the useRoutes hook
 * provided by React Router
 */
export function mapChildrenRoutes(
  childrenRoutes: Route[]
): RouteObject[] | undefined {
  return childrenRoutes.map((route) => ({
    path: route.path,
    element: route.pageElement,
    children: route.children ? mapChildrenRoutes(route.children) : undefined
  }))
}
/**
 * A version of routes that is mapped for specific usage in the App component,
 * to be passed into the useRoutes hook provided by React Router.
 */
export function getAppRoutes(isLoggedIn: boolean): () => RouteObject[] {
  // eslint-disable-next-line react/function-component-definition
  return (): RouteObject[] => [
    {
      element: <LoginPage />,
      index: true
    },
    {
      element: isLoggedIn ? <SelectCompanyPage /> : <Navigate to="/" replace />,
      path: '/select-company'
    },
    {
      element: isLoggedIn ? <AppShell /> : <Navigate to="/" replace />,
      children: mapChildrenRoutes(routes)
    },
    {
    // ! Temporarily commenting out all the tabs except for Site Readiness (ECS-5373)
    // ! Note: Remove these (!) comments when we reintroduce the pages
      element: isLoggedIn ? <Navigate to="/readiness" replace /> : <Navigate to="/" replace />,
      path: '*'
    }
  ]
}

function mapStateToProps(state: RootState) {
  return {
    user: state.user
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch) {
  return {
    dispatchValidateSession: (...args: Parameters<typeof validateSession>) => dispatch(validateSession(...args))
  }
}

type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>

type AppProps = StateProps & DispatchProps

export function App(
  {
    user,
    dispatchValidateSession
  }: AppProps
) {
  const location = useLocation()
  useEffect(() => {
    if (location.pathname !== '/' && user.isLoggedIn && user.sessionId && user.dataCenter && user.userId) {
      dispatchValidateSession({
        sessionId: user.sessionId,
        dataCenter: user.dataCenter,
        userId: user.userId
      })
    }
  }, [
    location.pathname,
    user.isLoggedIn,
    user.sessionId,
    user.dataCenter,
    user.userId
  ])
  const appRoutes = getAppRoutes(user.isLoggedIn)
  return useRoutes(appRoutes())
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
